import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SecondaryButton } from './ui/UI';
/**
 * Safety — the controls Periscan operates under.
 *
 * Previously: 14 lucide-icon principle tiles in a 4-column grid + a runner
 * capabilities sub-grid. Read as a wall of identical chips with vague icons
 * (Eye, Target, Lock, AlertTriangle, Database, etc) that didn't actually
 * explain anything specific.
 *
 * Now: a two-column editorial listing. Principles are named items in a
 * flowing list. Runner capabilities ditto. No icons. Specific verbs.
 */
const principles = ['Read-only access where possible', 'Scoped validation — nothing runs outside the agreed surface', 'Customer-controlled permissions, revocable any time', 'Clear test boundaries documented per mission', 'No destructive actions', 'No real data exfiltration', 'Approval gates for sensitive missions', 'Rate limits and test windows enforced server-side', 'Audit logs for every mission Periscan runs', 'Kill switch on by default', 'Plain-English evidence, not raw exploit output', 'Transparent behavior — you see what we did and why', 'Optional internal runner, outbound-only', 'Passive and active checks kept separate'];
const runnerCapabilities = ['Outbound-only communication', 'Scoped execution', 'Signed validation tasks', 'Policy-controlled testing', 'Rate limits', 'Audit logs', 'Local evidence collection', 'Internal reachability checks', 'Fix verification', 'BAS-lite & control-validation (planned)'];
export function Safety() {
  return <section className="relative w-full py-32 border-t border-white/5" data-id="element-1288">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1289">
        <SectionLabel number="—" label="Safety" className="mb-8" data-id="element-1290" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-id="element-1291">
          <div className="lg:col-span-7" data-id="element-1292">
            <motion.h2 initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.02em] leading-[1.05] text-white" data-id="element-1293">
              Validation should not
              <br data-id="element-1294" />
              <span className="text-white/40" data-id="element-1295">create a new incident.</span>
            </motion.h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4" data-id="element-1296">
            <p className="text-lg text-white/65 leading-relaxed mb-4" data-id="element-1297">
              Periscan is built for controlled, repeatable validation. Tests are
              scoped, observable, and built to help teams understand control
              performance — not to turn the environment into a science fair
              project.
            </p>
            <p className="text-base text-white leading-relaxed" data-id="element-1298">
              Adversarial enough to be useful. Controlled enough to be allowed
              near production.
            </p>
          </div>
        </div>

        {/* Principles — editorial two-column list */}
        <div className="mb-24" data-id="element-1299">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-6" data-id="element-1300">
            Operating principles
          </p>
          <ul className="border-t border-white/5 columns-1 md:columns-2 gap-x-12 [column-rule:1px_solid_rgba(255,255,255,0.05)]" data-id="element-1301">
            {principles.map((p, i) => <motion.li key={p} initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: i * 0.02
          }} className="border-b border-white/5 py-4 flex items-baseline gap-4 break-inside-avoid" data-id="element-1302">
                <span className="font-mono text-xs text-[#1a8fd4]/70 tracking-[0.2em] w-8 shrink-0" data-id="element-1303">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-base text-white/85 leading-snug" data-id="element-1304">
                  {p}
                </span>
              </motion.li>)}
          </ul>
        </div>

        {/* Internal Runner — typographic, no card gradient */}
        <div className="border-t border-white/10 pt-16" data-id="element-1305">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-1306">
            <div className="lg:col-span-5" data-id="element-1307">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#1a8fd4] mb-4" data-id="element-1308">
                Internal Runner
              </p>
              <h3 className="text-3xl md:text-4xl font-semibold text-white tracking-tight mb-6 leading-tight" data-id="element-1309">
                Optional runner for
                <br data-id="element-1310" />
                <span className="text-white/40" data-id="element-1311">deeper validation.</span>
              </h3>
              <p className="text-base text-white/60 leading-relaxed mb-8 max-w-md" data-id="element-1312">
                Some attack paths cannot be fully understood from the outside.
                The optional internal runner gives Periscan a safe, scoped way
                to validate internal exposure, reachability, and remediation
                status.
              </p>
              <SecondaryButton href="/early-access" size="md" data-id="element-1313">
                Join Internal Runner Waitlist
              </SecondaryButton>
              <p className="mt-4 text-xs text-white/40" data-id="element-1314">
                The internal runner is planned for later beta phases.
              </p>
            </div>
            <div className="lg:col-span-7" data-id="element-1315">
              <ul className="border-t border-white/5" data-id="element-1316">
                {runnerCapabilities.map((cap, i) => <motion.li key={cap} initial={{
                opacity: 0
              }} whileInView={{
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.3,
                delay: i * 0.03
              }} className="border-b border-white/5 py-3.5 flex items-baseline gap-4" data-id="element-1317">
                    <span className="font-mono text-xs text-[#1a8fd4]/60 tracking-[0.2em] w-8 shrink-0" data-id="element-1318">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-base text-white/85" data-id="element-1319">{cap}</span>
                  </motion.li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}